import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { url: String, frame: String, triggers: Array }

  connect() {
    this._onChange = (e) => {
      const target = e.target
      if (!target) return

      const fieldConfigs = this.fieldConfigsFor(target)
      if (fieldConfigs.length > 0) {
        if (this.blankValue(target)) {
          this.handleBlankFieldConfigs({ configs: fieldConfigs, target })
          return
        }

        fieldConfigs.forEach((config) => this.refreshDependentField({ config, target }))
        return
      }

      // ✅ 2º nível (por field): lê metadata no próprio <select>/<input>
      const key = target.dataset?.formTableDependentsKeyValue
      const frameId = target.dataset?.formTableDependentsFrameIdValue
      const paramName = target.dataset?.formTableDependentsParamNameValue

      if (key && frameId && paramName) {
        this.refreshFieldFrame({ key, frameId, paramName, target })
        return
      }

      // ✅ 1º nível (por triggers): seu comportamento atual
      const trigger = target.dataset?.formTableDependentsTrigger
      if (!trigger) return
      if (!this.hasTriggersValue || !this.triggersValue.includes(trigger)) return
      if (!this.hasUrlValue || !this.hasFrameValue) return

      this.refresh()
    }

    this.element.addEventListener("change", this._onChange)

    this._onFrameLoad = () => this._autoTriggerPreselected()
    this.element.addEventListener("turbo:frame-load", this._onFrameLoad)
    this.hydrateRootDependentFieldConfigs(this.element)
    this._autoRefreshDependentFields()
    this._autoRefreshTriggerFrames()
    this._autoTriggerPreselected()
  }

  // Busca todos os selects com metadata de dependent_frame e que já têm valor
  _autoTriggerPreselected() {
    this.element.querySelectorAll("[data-form-table-dependents-key-value]").forEach(target => {
      const key       = target.dataset.formTableDependentsKeyValue
      const frameId   = target.dataset.formTableDependentsFrameIdValue
      const paramName = target.dataset.formTableDependentsParamNameValue
      if (key && frameId && paramName && !this.blankValue(target)) {
        this.refreshFieldFrame({ key, frameId, paramName, target })
      }
    })
  }

  _autoRefreshDependentFields() {
    this.element.querySelectorAll("[data-form-table-dependents-field-configs-value]").forEach((target) => {
      if (this.blankValue(target)) return

      this.parseFieldConfigs(target).forEach((config) => {
        this.refreshDependentField({ config, target })
      })
    })
  }

  _autoRefreshTriggerFrames() {
    if (!this.hasUrlValue || !this.hasFrameValue) return
    if (!Array.isArray(this.triggersValue) || this.triggersValue.length === 0) return

    const hasAnyValue = this.triggersValue.some((t) => {
      const el = this.findTriggerElement(t)
      return el && !this.blankValue(el)
    })

    if (hasAnyValue) this.refresh()
  }

  disconnect() {
    if (this._onChange) this.element.removeEventListener("change", this._onChange)
    if (this._onFrameLoad) this.element.removeEventListener("turbo:frame-load", this._onFrameLoad)
  }

  // =========================
  // Nível 1 (por triggers)
  // =========================
  refresh() {
    const url = new URL(this.urlValue, window.location.origin)

    this.triggersValue.forEach((t) => {
      const el = this.findTriggerElement(t)
      const value = el ? this.controlValue(el) : ""

      if (value === null || value === undefined || value === "") {
        url.searchParams.delete(t)
      } else {
        url.searchParams.set(t, value)
      }
    })

    Turbo.visit(url.toString(), { frame: this.frameValue })
  }

  // =========================
  // Nível 2 (por field)
  // =========================
  refreshFieldFrame({ key, frameId, paramName, target }) {
    // se o frame não existir, Turbo faz navegação full-page
    const frameEl = document.getElementById(frameId)
    if (!frameEl) {
      console.warn(`[form-table-dependents] turbo-frame #${frameId} não encontrado`)
      return
    }

    const url = new URL(this.urlValue, window.location.origin)

    url.searchParams.set("key", key)
    url.searchParams.set("frame_id", frameId)

    const value = this.controlValue(target)
    if (value === null || value === undefined || value === "") {
      url.searchParams.delete(paramName)
    } else {
      url.searchParams.set(paramName, value)
    }

    // manda também os triggers atuais como contexto (plano_id, meio_pagamento_id, etc)
    ;(this.triggersValue || []).forEach((t) => {
      const el = this.findTriggerElement(t)
      const v = el ? this.controlValue(el) : ""
      if (v) url.searchParams.set(t, v)
    })

    Turbo.visit(url.toString(), { frame: frameId })
  }

  refreshDependentField({ config, target }) {
    const wrapperId = config.wrapper_id || config.wrapperId
    const urlValue = config.url

    if (!wrapperId || !urlValue) return

    const wrapper = document.getElementById(wrapperId)
    if (!wrapper) return

    const url = new URL(urlValue, window.location.origin)
    url.searchParams.set("key", config.key)
    url.searchParams.set("wrapper_id", wrapperId)

    const paramName = config.param_name || config.paramName
    const value = this.controlValue(target)

    if (paramName) {
      if (value === null || value === undefined || value === "") {
        if (config.refresh_when_blank || config.refreshWhenBlank) {
          url.searchParams.set(paramName, "")
        } else {
          url.searchParams.delete(paramName)
        }
      } else {
        url.searchParams.set(paramName, value)
      }
    }

    this.appendContextParams(url)

    fetch(url.toString(), {
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest"
      }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.text()
      })
      .then((html) => {
        wrapper.outerHTML = html
        const newWrapper = document.getElementById(wrapperId)
        if (newWrapper) {
          this.hydrateRootDependentFieldConfigs(newWrapper)
          this.clearBlankChildDependents(newWrapper)
        }
      })
      .catch((error) => {
        console.warn("[form-table-dependents] falha ao carregar dependent_field", error)
      })
  }

  clearBlankChildDependents(root) {
    this.fieldControlsWithin(root).forEach((target) => {
      if (!this.blankValue(target)) return

      this.handleBlankFieldConfigs({ configs: this.fieldConfigsFor(target), target })
    })
  }

  handleBlankFieldConfigs({ configs, target }) {
    configs.forEach((config) => {
      if (config.refresh_when_blank || config.refreshWhenBlank) {
        this.refreshDependentField({ config, target })
      } else {
        this.clearDependentWrapper(config)
      }
    })
  }

  clearDependentWrapper(config) {
    const wrapperId = config.wrapper_id || config.wrapperId
    if (!wrapperId) return

    const wrapper = document.getElementById(wrapperId)
    if (!wrapper) return

    this.fieldControlsWithin(wrapper).forEach((target) => {
      this.fieldConfigsFor(target).forEach((childConfig) => {
        this.clearDependentWrapper(childConfig)
      })
    })

    const control = this.fieldControlsWithin(wrapper)[0]
    if (!control?.name) {
      wrapper.innerHTML = ""
      wrapper.setAttribute("hidden", "hidden")
      return
    }

    const hidden = document.createElement("input")
    hidden.type = "hidden"
    hidden.id = wrapperId
    hidden.name = control.name
    hidden.value = ""

    wrapper.outerHTML = hidden.outerHTML
  }

  fieldControlsWithin(root) {
    if (!root) return []

    const selector = "input, select, textarea"
    const controls = []

    if (root.matches?.(selector)) controls.push(root)
    root.querySelectorAll?.(selector).forEach((control) => controls.push(control))

    return [...new Set(controls)]
  }

  blankValue(target) {
    if (target instanceof HTMLInputElement && target.type === "checkbox") {
      return false
    }

    if (target instanceof HTMLSelectElement && target.multiple) {
      return Array.from(target.selectedOptions).length === 0
    }

    const value = this.controlValue(target)
    return value === null || value === undefined || value === ""
  }

  controlValue(target) {
    if (target instanceof HTMLInputElement && target.type === "checkbox") {
      return target.checked ? (target.value || "1") : "0"
    }

    return target.value
  }

  appendContextParams(url) {
    ;(this.triggersValue || []).forEach((t) => {
      const el = this.findTriggerElement(t)
      const value = el ? this.controlValue(el) : ""

      if (value === null || value === undefined || value === "") {
        url.searchParams.delete(t)
      } else {
        url.searchParams.set(t, value)
      }
    })
  }

  parseFieldConfigs(target) {
    const raw = target.dataset?.formTableDependentsFieldConfigsValue
    if (!raw) return []

    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch (_error) {
      return []
    }
  }

  fieldConfigsFor(target) {
    const fieldConfigs = this.parseFieldConfigs(target)
    if (fieldConfigs.length > 0) return fieldConfigs

    return this.rootFieldConfigsFor(target)
  }

  hydrateRootDependentFieldConfigs(root) {
    if (!this.parseRootFieldConfigMap()) return

    this.fieldControlsWithin(root).forEach((target) => {
      if (target.dataset?.formTableDependentsFieldConfigsValue) return

      const configs = this.rootFieldConfigsFor(target)
      if (configs.length === 0) return

      target.dataset.formTableDependentsFieldConfigsValue = JSON.stringify(configs)
    })
  }

  rootFieldConfigsFor(target) {
    if (this.hiddenCheckablePeer(target)) return []

    const configMap = this.parseRootFieldConfigMap()
    if (!configMap) return []

    for (const trigger of this.triggerCandidatesFor(target)) {
      const configs = configMap[trigger]
      if (Array.isArray(configs) && configs.length > 0) return configs
    }

    return []
  }

  parseRootFieldConfigMap() {
    const raw = this.element.dataset?.formTableDependentsFieldConfigMapValue
    if (!raw) return null

    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null
    } catch (_error) {
      return null
    }
  }

  triggerCandidatesFor(target) {
    const candidates = [
      target.dataset?.formTableDependentsTrigger,
      this.paramNameFromInputName(target.name),
      target.name,
      target.id
    ].filter(Boolean)

    const configMap = this.parseRootFieldConfigMap()
    if (configMap) {
      Object.keys(configMap).forEach((trigger) => {
        if (target.name?.endsWith(`[${trigger}]`) || target.id?.endsWith(`_${trigger}`)) {
          candidates.push(trigger)
        }
      })
    }

    return [...new Set(candidates)]
  }

  paramNameFromInputName(name) {
    if (!name) return null

    const match = name.match(/\[([^\]]+)\]$/)
    return match ? match[1] : name
  }

  findTriggerElement(t) {
    // Prioriza o checkbox visível quando o Rails gera um hidden auxiliar.
    const escaped = this.escapeSelectorValue(t)
    const list = this.element.querySelectorAll(
      [
        `[data-form-table-dependents-trigger="${escaped}"]`,
        `[name="${escaped}"]`,
        `[name$="[${escaped}]"]`,
        `[id$="_${escaped}"]`
      ].join(", ")
    )
    if (!list || list.length === 0) return null

    const checkbox = Array.from(list).find((el) => el.type === "checkbox")
    if (checkbox) return checkbox

    return Array.from(list).find((el) => el.value && el.value !== "") || list[0]
  }

  hiddenCheckablePeer(target) {
    if (!(target instanceof HTMLInputElement) || target.type !== "hidden" || !target.name) return null

    const name = this.escapeSelectorValue(target.name)
    return this.element.querySelector(
      `input[type="checkbox"][name="${name}"]`
    )
  }

  escapeSelectorValue(value) {
    if (window.CSS?.escape) return window.CSS.escape(value)

    return String(value).replace(/["\\]/g, "\\$&")
  }
}
